import OnlyHeader from "components/Headers/OnlyHeader";
import WaitlistActionModal from "components/Modals/WaitlistActionModal";
import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import {
  getWaitlistedUsers,
  activateWaitlistedUser,
  deleteWaitlistedUser,
  searchWaitlistedUsers,
  filterWaitlistedUsers,
} from "store/actions/waitlistAction";

const Waitlist = () => {
  const dispatch = useDispatch();
  const { waitlistedUsers, totalPages, getWaitlistLoader, waitlistActionLoader } =
    useSelector((state) => state.waitlist);
  const { user: currentUser } = useSelector((state) => state.auth);

  const [actionModal, setActionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationStatus, setPaginationStatus] = useState("normal");
  const [statusFilter, setStatusFilter] = useState("all");

  const itemsPerPage = 10;

  useEffect(() => {
    // Only admin and sales should access this page
    if (currentUser?.role === "admin" || currentUser?.role === "sales") {
      dispatch(getWaitlistedUsers(1, itemsPerPage));
    }
  }, [dispatch, currentUser]);

  const toggleActionModal = () => {
    setActionModal(!actionModal);
  };

  const handleOpenActionModal = (user) => {
    setSelectedUser(user);
    toggleActionModal();
  };

  const handleAdmitAccess = () => {
    if (!selectedUser) return;
    
    dispatch(
      activateWaitlistedUser(selectedUser.id, () => {
        refreshCurrentView();
        toggleActionModal();
        setSelectedUser(null);
      })
    );
  };

  const handleDeleteAccount = () => {
    if (!selectedUser) return;
    
    dispatch(
      deleteWaitlistedUser(selectedUser.id, () => {
        refreshCurrentView();
        toggleActionModal();
        setSelectedUser(null);
      })
    );
  };

  const refreshCurrentView = () => {
    if (paginationStatus === "search") {
      dispatch(searchWaitlistedUsers(searchQuery, currentPage, statusFilter));
    } else if (paginationStatus === "filter") {
      dispatch(filterWaitlistedUsers(statusFilter, currentPage));
    } else {
      dispatch(getWaitlistedUsers(currentPage, itemsPerPage));
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    dispatch(searchWaitlistedUsers(searchQuery, 1, statusFilter));
    setCurrentPage(1);
    setPaginationStatus("search");
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    if (statusFilter !== "all") {
      dispatch(filterWaitlistedUsers(statusFilter, 1));
      setPaginationStatus("filter");
    } else {
      dispatch(getWaitlistedUsers(1, itemsPerPage));
      setPaginationStatus("normal");
    }
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    const newStatus = e.target.value;
    setStatusFilter(newStatus);
    setSearchQuery("");
    
    if (newStatus === "all") {
      dispatch(getWaitlistedUsers(1, itemsPerPage));
      setPaginationStatus("normal");
    } else {
      dispatch(filterWaitlistedUsers(newStatus, 1));
      setPaginationStatus("filter");
    }
    setCurrentPage(1);
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      if (paginationStatus === "search") {
        dispatch(searchWaitlistedUsers(searchQuery, newPage, statusFilter));
      } else if (paginationStatus === "filter") {
        dispatch(filterWaitlistedUsers(statusFilter, newPage));
      } else {
        dispatch(getWaitlistedUsers(newPage, itemsPerPage));
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);

    const formattedDate = date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });

    return formattedDate;
  };

  // Check if user is admin or sales
  if (currentUser?.role !== "admin" && currentUser?.role !== "sales") {
    return (
      <>
        <OnlyHeader />
        <Container className="mt--7" fluid>
          <Row>
            <div className="col">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <h3 className="mb-0">Access Denied</h3>
                </CardHeader>
                <div className="p-4">
                  <p>You do not have permission to access this page.</p>
                </div>
              </Card>
            </div>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="d-flex flex-column justify-content-between align-items-center flex-xl-row flex-wrap">
                  <Col className="col-lg-2 col-12 text-center text-lg-left mb-3 mb-lg-0">
                    <h3 className="mb-0">Waitlist</h3>
                    <p className="text-sm text-muted mb-0">
                      Service Providers Only
                    </p>
                  </Col>
                  <Col className="d-flex flex-column flex-lg-row gap-x-10 justify-content-around align-items-center align-items-lg-start w-100">
                    <div>
                      <FormGroup className="w-100">
                        <Input
                          id="waitlistSearch"
                          name="Search"
                          placeholder="Search Here..."
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleSearch();
                            }
                          }}
                          className="py-2"
                        />
                        <small className="text-xs text-primary d-block text-center text-lg-left">
                          Search by ID, Email & Name
                        </small>
                      </FormGroup>
                    </div>
                    <div className="d-flex flex-wrap justify-content-start justify-content-lg-start align-items-center">
                      <Button
                        color="primary"
                        className="px-3 py-2 mx-2 my-2"
                        disabled={getWaitlistLoader}
                        onClick={handleSearch}
                        size="sm"
                      >
                        Search
                      </Button>
                      <Button
                        color="primary"
                        className="p-2 mx-2 my-2"
                        disabled={getWaitlistLoader || !searchQuery}
                        onClick={handleClearSearch}
                        size="sm"
                      >
                        Clear Search
                      </Button>
                      <div className="position-relative d-inline-block mx-2 my-2" style={{ minWidth: "150px" }}>
                        <Input
                          className="form-control"
                          name="statusFilter"
                          type="select"
                          value={statusFilter}
                          onChange={handleStatusFilterChange}
                          disabled={getWaitlistLoader}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="waitlisted">Waitlisted</option>
                        </Input>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">User ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Status</th>
                    <th scope="col">Created At</th>
                    {currentUser?.role !== "sales" && <th scope="col">Actions</th>}
                  </tr>
                </thead>
                {!getWaitlistLoader ? (
                  <tbody>
                    {waitlistedUsers && waitlistedUsers.length > 0 ? (
                      waitlistedUsers.map((waitlistUser, index) => {
                        return (
                          <tr key={index}>
                            <td>{waitlistUser?.userId || "N/A"}</td>
                            <td>
                              {waitlistUser?.firstName} {waitlistUser?.lastName}
                            </td>
                            <td>{waitlistUser?.email || "N/A"}</td>
                            <td>{waitlistUser?.phoneNumber || "N/A"}</td>
                            <td>
                              <span className={`badge ${waitlistUser?.waitlisted ? 'badge-warning' : 'badge-success'}`}>
                                {waitlistUser?.waitlisted ? "WAITLISTED" : "ACTIVE"}
                              </span>
                            </td>
                            <td>{formatTimestamp(waitlistUser?.createdAt)}</td>
                            {currentUser?.role !== "sales" && (
                              <td>
                                <Button
                                  size="sm"
                                  color="primary"
                                  disabled={waitlistActionLoader || !waitlistUser?.waitlisted}
                                  onClick={() => handleOpenActionModal(waitlistUser)}
                                  style={{
                                    cursor: (waitlistActionLoader || !waitlistUser?.waitlisted) ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  Manage Access
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={currentUser?.role === "sales" ? "6" : "7"} style={{ textAlign: "center" }}>
                          <p className="mb-0 text-sm">No users found.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={currentUser?.role === "sales" ? "6" : "7"} style={{ textAlign: "center" }}>
                        <Spinner
                          size="lg"
                          style={{ display: "block", margin: "0 auto" }}
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
              </Table>
              {totalPages > 0 && (
                <CardFooter className="py-4">
                  <nav aria-label="...">
                    <ReactPaginate
                      previousLabel={"<"}
                      nextLabel={">"}
                      breakLabel={"..."}
                      breakClassName={"break-me"}
                      pageCount={totalPages}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={2}
                      onPageChange={handlePageChange}
                      forcePage={currentPage - 1}
                      containerClassName={
                        "pagination px-1 justify-content-end mb-0"
                      }
                      activeClassName={"active"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      nextClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextLinkClassName={"page-link"}
                    />
                  </nav>
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      <WaitlistActionModal
        modal={actionModal}
        toggle={toggleActionModal}
        onAdmit={handleAdmitAccess}
        onDelete={handleDeleteAccount}
        loading={waitlistActionLoader}
        userName={selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ""}
      />
    </>
  );
};

export default Waitlist;
